import { ReactNode, DetailedHTMLProps } from 'react';
import { Formik, FormikConfig, FormikValues } from 'formik';

import { FormCallbacks } from './Callbacks';

type FormProps = Omit<
    DetailedHTMLProps<React.FormHTMLAttributes<HTMLFormElement>, HTMLFormElement>,
    'onReset' | 'onSubmit' | 'ref'
>;

interface PropsWithInitialValues<V>
    extends Omit<FormikConfig<V>, 'render' | 'children' | 'component' | 'innerRef'>,
        FormProps {
    children: ReactNode;
    values?: V;
    onValidationChange?: (isValid: boolean) => void;
}

interface PropsWithValues<V>
    extends Omit<FormikConfig<V>, 'initialValues' | 'render' | 'children' | 'component' | 'innerRef'>,
        FormProps {
    values: V;
    initialValues?: V;
    children: ReactNode;
    onValidationChange?: (isValid: boolean) => void;
}

type Props<V> = PropsWithInitialValues<V> | PropsWithValues<V>;

export const Form = <V extends FormikValues = FormikValues>({
    children,
    initialValues,
    initialErrors,
    initialStatus,
    initialTouched,
    validationSchema,
    validateOnBlur,
    validateOnChange,
    validateOnMount,
    values,
    validate,
    onSubmit,
    onReset,
    onValidationChange,
    ...props
}: Props<V>) => (
    <Formik
        {...{
            initialValues: (values || initialValues) as V,
            initialErrors,
            initialStatus,
            initialTouched,
            validationSchema,
            validateOnBlur,
            validateOnChange,
            validateOnMount,
            validate,
            onSubmit,
            onReset,
        }}
    >
        {({ handleSubmit }) => (
            <form {...props} onSubmit={handleSubmit}>
                {children}
                <FormCallbacks {...{ initialValues, values }} onValidationChange={onValidationChange} />
            </form>
        )}
    </Formik>
);
